import { Component, inject, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { ActivatedRouteSnapshot } from '@angular/router';
import { RouterLink } from '@angular/router';
import type { MenuItem } from 'primeng/api';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { APP_NAME } from 'app/core/tokens/app-name.token';
import { ButtonModule } from 'primeng/button';
import { TranslateModule } from '@ngx-translate/core';
import { IUserMenuOption } from 'app/shared/interfaces/IMainLayout';
import { AuthService } from 'app/core/services/auth/auth.service';
import { USER_ROLE_LABEL_KEY } from 'app/core/constants/user-role-label-key.constant';
import { NotificationBellComponent } from 'app/shared/components/notification-bell/notification-bell.component';
import { UserMenuComponent } from "../user-menu/user-menu.component";
import { initials } from 'app/shared/utils/format.util';

@Component({
  selector: 'navbar',
  standalone: true,
  imports: [CommonModule, RouterLink, ButtonModule, NotificationBellComponent, UserMenuComponent, TranslateModule],
  templateUrl: './navbar.component.html',
})
export class NavbarComponent {
  private router = inject(Router);
  readonly appName = inject(APP_NAME);
  readonly authService = inject(AuthService);
  readonly initials = initials;

  // Mapa UserRole -> key i18n (`common.roles.*`); se traduce con el pipe en el template.
  UserRole = USER_ROLE_LABEL_KEY;

  readonly items = signal<MenuItem[]>([]);

  readonly home: MenuItem = { icon: 'pi pi-home', routerLink: '/knowledge-base' };

  readonly userMenuOpen = signal(false);
  @ViewChild(UserMenuComponent) UserMenu!: UserMenuComponent;

  selectedOption = null;

  toggleUserMenu(ev: MouseEvent) {
    this.UserMenu.anchor(ev); // solo ancla (posicionamiento)
    this.userMenuOpen.set(!this.userMenuOpen());
  }

  options: IUserMenuOption[] = [
    /*
    {
      id: "1",
      name: 'Mi perfil',
      image: 'profile.svg',
      action: () => console.log("Ir al perfil")
    },
    {
      id: "2",
      name: 'Abrir Suite',
      image: 'suite.svg',
      action: () => console.log("Ir a la suite")
    },
    */
    {
      id: "3",
      name: 'common.actions.logout',
      image: 'log-out.svg',
      // logoutEverywhere(): bajo el realm cierra también la sesión del IdP; en el camino
      // legado es exactamente el logout() de siempre (Task 11d).
      action: () => this.authService.logoutEverywhere()
    },
  ];

  constructor() {
    // Recalcular en cada navegación
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntilDestroyed())
      .subscribe(() => this.items.set(this.build()));

    this.items.set(this.build());
  }

  private build(): MenuItem[] {
    const out: MenuItem[] = [];
    let url = '';

    let snap: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;

    while (snap) {
      // Segmentos de esta rama (puede ser [])
      const seg = (snap.url ?? []).map(s => s.path).join('/');
      if (seg) url += `/${seg}`;

      const d: any = snap.data ?? {};

      // Si esta ruta pertenece a un "grupo" (Configuración), se inserta una sola vez
      if (d.group && !out.some(i => i.label === d.group)) {
        out.push({ label: d.group, icon: d.groupIcon ?? 'pi pi-cog' });
      }

      // Si esta ruta define breadcrumb, lo agregamos con su link acumulado
      if (d.breadcrumb) {
        out.push({
          label: d.breadcrumb,
          routerLink: url || '/', // por si queda vacío
          icon: d.icon, // opcional
        });
      }

      // bajar al primer hijo
      snap = snap.firstChild ?? null;
    }

    return out;
  }
}